/**
 * ADR-141 D2 — the single mint authority for the analytics correlation token.
 *
 * One action mints one token; that token appears in exactly one Umami-plane row
 * and one Class-1-plane row. Zero means no join; more than one on either side
 * REFUSES the join and alerts. Time and surface never become a fallback key.
 *
 * ⚠️ **This module is deliberately tiny and deliberately argument-free.** D2's
 * admissibility conditions are mostly negative — "no email, account id, tenant
 * id, user id, device fingerprint, Telegram id, timestamp, counter, hash or
 * HMAC input enters minting" — and a negative is not something a function body
 * can assert about itself. `mintCorrelationToken()` takes **no parameters**, so
 * there is no channel through which a caller-derived value could reach the
 * mint. That is the enforcement: not a check, an absent parameter list.
 *
 * ⭐ What this does NOT prove, stated because the gap is the whole reason
 * D9.12 exists: the type system stops a caller passing derivation INPUT here,
 * and stops a plain `string` being written where a `CorrelationToken` is
 * expected. It does not stop a caller minting its own value somewhere else and
 * casting it, and it never could — ADR-141 D2.1 puts it as "sink format alone
 * cannot prove non-derivation". A 32-hex-character string derived from an
 * email hash is indistinguishable from this one by inspection. The AST rule in
 * `@agnivo/config/eslint` (`no-unminted-correlation-token`) is what closes
 * that, by checking the call graph rather than the value.
 */
/** Bytes of CSPRNG output per token. ADR-141 D2.1 requires **at least** 128 bits. */
const TOKEN_BYTES = 16;
const TOKEN_PATTERN = /^[0-9a-f]{32}$/;
/** Raised when no CSPRNG is reachable. Never caught inside this module. */
export class CorrelationTokenUnavailableError extends Error {
    constructor() {
        super('No Web Crypto CSPRNG available; refusing to mint a correlation token. ' +
            'There is no non-CSPRNG fallback by design (ADR-141 D2.1).');
        this.name = 'CorrelationTokenUnavailableError';
    }
}
/**
 * Mint one token for one action.
 *
 * ⛔ **Throws rather than falling back.** `Math.random` is not a CSPRNG and a
 * predictable token is worse than an absent one: the join would still succeed
 * and would silently carry a guessable correlation key. Every other failure
 * mode in this telemetry subsystem is swallowed (see `clientEvent.ts` — fire
 * and forget, never throws); this one is not, and the asymmetry is deliberate.
 * Callers that cannot tolerate a throw must decide NOT to emit, which is the
 * correct behaviour — D2.2 says zero rows simply means no join.
 *
 * ⛔ **Callers must not retry an Umami send with the same token, or with a new
 * one for the same action.** Umami's send is fire-and-forget with no
 * idempotency key, so a retry manufactures the second Umami-plane row that
 * breaks D2.2's exactly-1+1 cardinality check — and the check refuses the join
 * rather than picking one, so a retry destroys the measurement it was trying to
 * save. A Class-1-plane retry reuses its server-bound token instead.
 */
export function mintCorrelationToken() {
    const webcrypto = globalThis.crypto;
    if (!webcrypto || typeof webcrypto.getRandomValues !== 'function') {
        throw new CorrelationTokenUnavailableError();
    }
    const bytes = new Uint8Array(TOKEN_BYTES);
    webcrypto.getRandomValues(bytes);
    let hex = '';
    for (const byte of bytes) {
        hex += byte.toString(16).padStart(2, '0');
    }
    return hex;
}
/**
 * Format check for the token encoding — the client-side mirror of the
 * gateway's D2.3 server-side validation.
 *
 * ⚠️ **This is a FORMAT predicate, not an admissibility one.** It says the
 * string is 32 lowercase hex characters. It says nothing about whether the
 * value was minted here, and a caller must never read a `true` from it as
 * evidence that D2's derivation rules were honoured — that is precisely the
 * "sink format" reasoning D2.1 rules out. It exists for tests and for
 * rejecting malformed input at a boundary, not for admitting a token.
 */
export function isCorrelationToken(value) {
    return TOKEN_PATTERN.test(value);
}
//# sourceMappingURL=correlationToken.js.map